import type { GeneratedApp } from '@/types';

export interface AppDocument {
  name?: string;
  title?: string;
  description?: string;
  summary?: string;
  components?: AppDocumentComponent[];
  suggestions?: string[];
}

export type AppDocumentComponent = Record<string, unknown> & { type: string };

export function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

export function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

export function asMatrix(v: unknown): string[][] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) => row.map((cell) => String(cell ?? '')));
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

export function isJsonLike(text: string): boolean {
  const t = text.trim();
  return t.startsWith('{') || t.startsWith('[');
}

export function tryParseDocumentJson(raw: unknown): AppDocument | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (!t.startsWith('{')) return null;
  try {
    return documentFromRecord(JSON.parse(t) as Record<string, unknown>);
  } catch {
    const start = t.indexOf('{');
    const end = t.lastIndexOf('}');
    if (start < 0 || end <= start) return null;
    try {
      return documentFromRecord(JSON.parse(t.slice(start, end + 1)) as Record<string, unknown>);
    } catch {
      return null;
    }
  }
}

export function documentFromRecord(p: Record<string, unknown>): AppDocument | null {
  const components = Array.isArray(p.components) ? (p.components as AppDocumentComponent[]) : null;
  if (components && components.length > 0) {
    return {
      name: str(p.name),
      title: str(p.title) ?? str(p.name),
      description: str(p.description),
      summary: str(p.summary) ?? str(p.description),
      components,
      suggestions: asStringArray(p.suggestions),
    };
  }
  return buildDocumentFromPayload(p, asRecord(p.payload) ?? p);
}

export function buildDocumentFromPayload(
  meta: Record<string, unknown>,
  payload: Record<string, unknown>,
): AppDocument | null {
  const title = str(meta.title) ?? str(meta.name) ?? 'Generated app';
  const summary = str(meta.summary) ?? str(meta.description);
  const format = String(meta.format ?? 'comparison').toLowerCase();
  const components: AppDocumentComponent[] = [{ type: 'header', title }];

  const columns = payload.columns;
  const rows = payload.rows;
  if (asStringArray(columns).length > 0 || asMatrix(rows).length > 0) {
    components.push({
      type: format === 'data_aggregation' ? 'data_table' : 'comparison_table',
      columns,
      rows,
    });
    return { title, name: title, summary, description: summary, components };
  }

  if (Array.isArray(payload.events) && payload.events.length > 0) {
    components.push({ type: 'timeline', events: payload.events });
    return { title, name: title, summary, components };
  }

  const widgets = payload.widgets;
  if (format === 'dashboard' && Array.isArray(widgets) && widgets.length > 0) {
    if (summary) components.push({ type: 'summary', text: summary });
    components.push({ type: 'dashboard_metrics', widgets });
    if (Array.isArray(payload.sections)) {
      components.push({ type: 'dashboard_sections', sections: payload.sections });
    }
    if (Array.isArray(payload.sources)) {
      components.push({ type: 'sources', items: payload.sources });
    }
    return { title, name: title, summary, description: summary, components };
  }

  if (Array.isArray(payload.sections) && payload.sections.length > 0) {
    if (summary) components.push({ type: 'summary', text: summary });
    components.push({ type: 'research_sections', sections: payload.sections });
    if (Array.isArray(payload.sources)) {
      components.push({ type: 'sources', items: payload.sources });
    }
    return { title, name: title, summary, components };
  }

  if (Array.isArray(payload.collections) && payload.collections.length > 0) {
    components.push({ type: 'collections', groups: payload.collections });
    return { title, name: title, summary, components };
  }

  if (Array.isArray(payload.entities) && payload.entities.length > 0) {
    components.push({ type: 'entities', items: payload.entities });
    return { title, name: title, summary, components };
  }

  if (Array.isArray(widgets) && widgets.length > 0) {
    if (summary) components.push({ type: 'summary', text: summary });
    components.push({ type: 'dashboard_metrics', widgets });
    if (Array.isArray(payload.sections)) {
      components.push({ type: 'dashboard_sections', sections: payload.sections });
    }
    if (Array.isArray(payload.sources)) {
      components.push({ type: 'sources', items: payload.sources });
    }
    return { title, name: title, summary, description: summary, components };
  }

  return null;
}

function hasDashboardMetrics(components: AppDocumentComponent[] | undefined): boolean {
  return (
    components?.some((c) => c.type === 'dashboard_metrics' || c.type === 'dashboard_widgets') ?? false
  );
}

function widgetsFromSectionBullets(sections: unknown): Record<string, unknown>[] {
  if (!Array.isArray(sections)) return [];
  const widgets: Record<string, unknown>[] = [];
  for (const sec of sections) {
    const s = asRecord(sec);
    const bullets = asStringArray(s?.bullets);
    for (const bullet of bullets) {
      const match = bullet.match(/^(.+?)\s+\(([^)]+)\)\s*$/);
      widgets.push({
        title: match?.[1] ?? bullet,
        subtitle: match?.[2] ?? 'Source',
        value: 'View data',
        change: 'Tracked',
      });
    }
  }
  return widgets;
}

function isGenericDashboardTitle(title: string, tabHint?: number): boolean {
  const t = title.trim();
  if (!t || t === 'Dashboard headline' || t === 'Insights Dashboard') return true;
  if (tabHint != null && tabHint > 0 && new RegExp(`^Generated from ${tabHint} tabs$`, 'i').test(t)) {
    return true;
  }
  return /^Generated from \d+ tabs$/i.test(t);
}

function widgetsFromSourceItems(items: unknown[]): Record<string, unknown>[] {
  const widgets: Record<string, unknown>[] = [];
  for (const item of items) {
    const s = asRecord(item);
    if (!s) continue;
    const title = typeof s.title === 'string' ? s.title : 'Source';
    const url = typeof s.url === 'string' ? s.url : '';
    widgets.push({
      title,
      subtitle: 'Data source',
      value: 'View data',
      change: 'Tracked',
      source_title: title,
      source_url: url,
    });
  }
  return widgets;
}

function widgetsFromDashboardTitle(title: string): Record<string, unknown>[] {
  const subject = title.includes(':') ? title.split(':').slice(1).join(':').trim() : title.trim();
  const parts = subject
    .split(/[,&]/)
    .map((part) => part.replace(/\btrends?\b/gi, '').trim())
    .filter((part) => part.length > 1);
  if (parts.length < 2) return [];
  return parts.map((part) => ({
    title: part,
    subtitle: 'Indicator',
    value: '—',
    change: 'Open source for latest',
  }));
}

function sourcesFromDocument(doc: AppDocument | null): unknown[] {
  const sourcesComp = doc?.components?.find((c) => c.type === 'sources');
  if (sourcesComp && Array.isArray(sourcesComp.items)) {
    return sourcesComp.items;
  }
  return [];
}

/** Upgrade legacy dashboard apps that only stored header + summary. */
export function enrichDashboardDocument(
  app: GeneratedApp,
  doc: AppDocument | null,
): AppDocument | null {
  const rawType = String(app.raw_app_type ?? app.app_data?.format ?? '').toLowerCase();
  if (rawType !== 'dashboard') return doc;

  const appTitle = app.name?.trim() || 'Dashboard';
  const title =
    doc?.title && !isGenericDashboardTitle(doc.title)
      ? doc.title
      : appTitle;
  const summary = doc?.summary ?? doc?.description ?? app.description;
  const payload = asRecord(app.app_data?.payload) ?? asRecord(app.app_data) ?? {};

  if (doc && hasDashboardMetrics(doc.components)) {
    const header = doc.components?.find((c) => c.type === 'header');
    const headerTitle = typeof header?.title === 'string' ? header.title : '';
    if (isGenericDashboardTitle(headerTitle) && !isGenericDashboardTitle(title)) {
      const components = (doc.components ?? []).map((c) =>
        c.type === 'header' ? { ...c, title } : c,
      );
      return { ...doc, title, name: title, components };
    }
    return doc;
  }

  const widgetsRaw = payload.widgets ?? app.app_data?.widgets;
  let widgets: unknown[] = Array.isArray(widgetsRaw) ? widgetsRaw : [];

  if (widgets.length === 0) {
    widgets = widgetsFromSourceItems(sourcesFromDocument(doc));
  }
  if (widgets.length === 0 && Array.isArray(payload.sources)) {
    widgets = widgetsFromSourceItems(payload.sources);
  }
  if (widgets.length === 0 && payload.sections) {
    widgets = widgetsFromSectionBullets(payload.sections);
  }
  if (widgets.length === 0) {
    widgets = widgetsFromDashboardTitle(appTitle);
  }
  if (widgets.length === 0 && app.source_url) {
    widgets = [
      {
        title: app.source_title ?? appTitle,
        subtitle: 'Primary source',
        value: 'View data',
        change: 'Tracked',
        source_title: app.source_title ?? appTitle,
        source_url: app.source_url,
      },
    ];
  }

  if (widgets.length === 0) return doc;

  const sections =
    Array.isArray(payload.sections) && payload.sections.length > 0
      ? payload.sections
      : summary
        ? [{ title: 'Overview', bullets: [summary] }]
        : undefined;

  const components: AppDocumentComponent[] = [{ type: 'header', title }];
  if (summary) components.push({ type: 'summary', text: summary });
  components.push({ type: 'dashboard_metrics', widgets });
  if (sections) {
    components.push({ type: 'dashboard_sections', sections });
  }
  const sourceItems = sourcesFromDocument(doc);
  if (sourceItems.length > 0) {
    components.push({ type: 'sources', items: sourceItems });
  } else if (Array.isArray(payload.sources)) {
    components.push({ type: 'sources', items: payload.sources });
  }

  return {
    name: title,
    title,
    description: summary,
    summary,
    components,
    suggestions: asStringArray(app.app_data?.suggestions),
  };
}

/** Normalize stored app_data so components are always available when parseable. */
export function hydrateAppData(
  appData: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!appData) return null;

  const direct = documentFromRecord(appData);
  if (direct?.components?.length) {
    return {
      ...appData,
      components: direct.components,
      title: direct.title ?? appData.title,
      summary: direct.summary ?? appData.summary,
      description: direct.description ?? appData.description,
      suggestions: direct.suggestions ?? appData.suggestions,
      render_mode: 'components',
    };
  }

  const next = { ...appData };
  for (const key of ['content', 'description', 'summary'] as const) {
    const parsed = tryParseDocumentJson(next[key]);
    if (parsed?.components?.length) {
      next.components = parsed.components;
      if (!next.title && parsed.title) next.title = parsed.title;
      if (!next.summary && parsed.summary) next.summary = parsed.summary;
      if (!next.description && parsed.description) next.description = parsed.description;
      if (!next.suggestions && parsed.suggestions) next.suggestions = parsed.suggestions;
      if (key === 'content') delete next.content;
      next.render_mode = 'components';
      break;
    }
  }

  return next;
}

export function resolveAppDocument(app: GeneratedApp): AppDocument | null {
  const data = hydrateAppData(app.app_data ?? null);

  let doc: AppDocument | null = null;
  if (data) {
    doc = documentFromRecord(data);
  }

  if (!doc) {
    for (const raw of [app.generated_code, app.description, app.app_description]) {
      const parsed = tryParseDocumentJson(raw);
      if (parsed) {
        doc = parsed;
        break;
      }
    }
  }

  return enrichDashboardDocument(app, doc);
}

export function getAppCardDescription(app: GeneratedApp): string | undefined {
  const doc = resolveAppDocument(app);
  if (doc?.summary) return doc.summary;
  if (doc?.description) return doc.description;

  for (const candidate of [app.description, app.app_description]) {
    if (typeof candidate !== 'string' || !candidate.trim()) continue;
    if (isJsonLike(candidate)) {
      const parsed = tryParseDocumentJson(candidate);
      if (parsed?.description) return parsed.description;
      if (parsed?.summary) return parsed.summary;
      continue;
    }
    if (!/^Content from \d+ tabs$/i.test(candidate.trim())) {
      return candidate.trim();
    }
  }

  const data = app.app_data;
  const summary = typeof data?.summary === 'string' ? data.summary : undefined;
  if (summary && !isJsonLike(summary)) return summary;

  return undefined;
}

const CARD_PREVIEW_TYPES = new Set([
  'header',
  'comparison_table',
  'data_table',
  'summary',
  'timeline',
  'dashboard_metrics',
  'dashboard_widgets',
]);

export function cardPreviewDocument(doc: AppDocument): AppDocument {
  const components = doc.components ?? [];
  const header = components.find((c) => c.type === 'header');
  const metrics = components.find(
    (c) => c.type === 'dashboard_metrics' || c.type === 'dashboard_widgets',
  );
  const table = components.find((c) => c.type === 'comparison_table' || c.type === 'data_table');
  const summary = components.find((c) => c.type === 'summary');

  const picked: AppDocumentComponent[] = [];
  if (header) picked.push(header);
  if (metrics) picked.push(metrics);
  else if (table) picked.push(table);
  else if (summary) picked.push(summary);

  if (picked.length === 0) {
    return {
      ...doc,
      components: components.filter((c) => CARD_PREVIEW_TYPES.has(c.type)).slice(0, 2),
      suggestions: [],
    };
  }

  return { ...doc, components: picked, suggestions: [] };
}
